import Avatar from '@/components/Avatar'
import Button from '@/components/Button'
import LivePulse from '@/components/LivePulse'
import StatusBadge from '@/components/StatusBadge'
import Text from '@/components/Text'
import { formatJsonLd } from '@/compositions/MetaInfo'
import { ModalProvider } from '@/context/modal'
import { GeneratingPage } from '@/lib/eventsTracker'
import {
  JoinBookedSessionCardAction,
  JoinLiveSessionCardAction,
  LivenessType,
  MustAuthenticateUserCardAction,
  NavigateToClassDetailAction,
  SessionCard,
  SessionCardAccessDisplay,
  SessionSchema,
  Sponsor,
  ViewClassDetailCardAction,
} from '@/shared/api-generated-types'
import Badges, { BadgesTheme } from '@/widgets/Badges'
import { useTranslation } from 'next-i18next'
import React, { ReactElement } from 'react'
import DialogAction from '../DialogAction'
import Feed from './render/Feed'
import Standard from './render/Standard'
import * as styles from './style'

type PossibleString = string | null | undefined

export interface Props {
  vodClass?: boolean
  badgesTheme?: BadgesTheme
  canBookmark?: boolean
  canRemind?: boolean
  durationMinutes?: number
  end?: string | undefined
  footerContent?: React.ReactNode | null
  href?: PossibleString
  id?: PossibleString
  classId?: PossibleString
  imageUrl?: PossibleString
  accessDisplay?: SessionCardAccessDisplay
  guideAvatarUrl?: PossibleString
  live?: boolean | undefined
  rating?: number | null
  start?: string | undefined
  title?: string | undefined
  feedItem?: boolean
  reactionCount?: number
  viewCount?: number
  commentCount?: number
  guideByline?: SessionCard['guideByline']
  navigationAction?: SessionCard['navigationAction']
  primaryAction?: SessionCard['primaryAction']
  secondaryAction?: SessionCard['secondaryAction']
  navigateToGuideAction?: SessionCard['navigateToGuideAction']
  livenessType?: SessionCard['livenessType']
  fluid?: boolean
  wide?: boolean
  isSchedule?: boolean
  fixedAspectRatio?: boolean
  metaInfo?: {
    jsonLd?: SessionSchema
  }
  searchResultIndex?: number // 70e7b1d8-0cb9-4883-8927-1a69372cfac2
  generatingPage?: GeneratingPage
  isCommunityLedClass?: boolean
  isEncore?: boolean
  classIsNew?: boolean
  sessionId: string
  sponsor?: Sponsor
  bookmarked?: boolean
}

export interface VariantProps extends Props {
  isLive: boolean
  handleAction: (event: React.MouseEvent) => void
}

export const GuideByline = ({
  badgesTheme,
  guideAvatarUrl,
  guideByline,
  isCommunityLedClass,
  isEncore,
  navigateToGuideAction,
  livenessType,
}: Partial<Props>): JSX.Element | null => {
  const { t } = useTranslation('common')

  return navigateToGuideAction?.url && guideAvatarUrl && guideByline ? (
    <div className={styles.guideBadgeContainer}>
      <span
        onClick={(e) => {
          e.preventDefault()
          window.location.href = navigateToGuideAction.url
        }}
      >
        <Text className="flex-center" size="xxs" weight="md" tag="div">
          <Avatar size="xs" src={guideAvatarUrl} className="pr-sm" />
          <span className="pl-xxs ellipsis-1L "> {guideByline}</span>
        </Text>
      </span>
      <Badges
        isCommunityLedClass={isCommunityLedClass}
        isEncore={isEncore}
        isVideo={livenessType === LivenessType.Live}
        size="SM"
        theme={'MINIMAL'}
        rounded
        overlappingBadges
      />
    </div>
  ) : guideAvatarUrl && guideByline ? (
    <div className={styles.guideBadgeContainer}>
      <Text className="flex-center" size="xs" weight="md" tag="div">
        <Avatar size="xs" src={guideAvatarUrl} className="pr-sm" />
        <span className="pl-xxs"> {guideByline}</span>
      </Text>
      <Badges isCommunityLedClass={isCommunityLedClass} isEncore={isEncore} size="SM" theme={badgesTheme} />
    </div>
  ) : null
}

export const LiveListRemind = ({ isLive, canRemind, canBookmark }: Partial<VariantProps>): JSX.Element => (
  <>
    {isLive ? (
      <div data-testid="live-indicator" className={styles.liveStatus}>
        <StatusBadge>
          <LivePulse /> Live - Just started
        </StatusBadge>
      </div>
    ) : null}
    {canBookmark || canRemind ? (
      <div>
        <div data-testid="class-bookmark-remind" className={styles.secondaryAction}>
          {canBookmark ? (
            <Button size="md" component="link" theme="smoke" iconLeft="plus" label="My List" />
          ) : canRemind ? (
            <Button size="md" iconLeft="bell" theme="smoke" label="Remind Me" />
          ) : null}
        </div>
      </div>
    ) : null}
  </>
)

export const CardPrimaryAction = React.memo(function CardPrimaryAction({
  primaryAction,
  navigationAction,
  makeBooking,
  children,
}: {
  primaryAction: SessionCard['primaryAction']
  navigationAction?: SessionCard['navigationAction']
  makeBooking?: (sessionId: string) => void
  children: ReactElement
}): ReactElement {
  const action = primaryAction?.__typename ?? undefined
  let actionButton: ReactElement | undefined = undefined
  // External links are not for the next/link component
  if (action === 'JoinBookedSessionCardAction' || action === 'JoinLiveSessionCardAction') {
    actionButton = React.cloneElement(children, {
      component: 'link',
      href: (primaryAction as JoinBookedSessionCardAction | JoinLiveSessionCardAction).url,
    })
  } else if (action === 'MustAuthenticateUserCardAction') {
    actionButton = React.cloneElement(children, {
      component: 'a', // TODO: This might be able to be changed to use next/link
      href: (primaryAction as MustAuthenticateUserCardAction).url,
    })
  } else if (action === 'ViewClassDetailCardAction') {
    actionButton = React.cloneElement(children, {
      component: 'link',
      href: (primaryAction as ViewClassDetailCardAction).url,
    })
  } else if (action === 'BookSessionCardAction') {
    actionButton = React.cloneElement(children, {
      component: 'button',
      onClick: makeBooking,
    })
  } else if (navigationAction) {
    actionButton = React.cloneElement(children, {
      component: 'link',
      href: (navigationAction as NavigateToClassDetailAction).url,
    })
  }

  if (primaryAction?.__typename === 'GetAccessSessionCardAction' && primaryAction?.dialogType) {
    return (
      <ModalProvider>
        <DialogAction action={primaryAction} />
      </ModalProvider>
    )
  }

  return <>{actionButton}</>
})

export const CardSecondaryAction = React.memo(function CardSecondaryAction({
  secondaryAction,
  cancelBooking,
  children,
}: {
  secondaryAction: SessionCard['secondaryAction']
  cancelBooking?: (sessionId: string) => void
  children: ReactElement
}): ReactElement {
  const action = secondaryAction?.__typename ?? undefined

  if (action === 'CancelSessionBookingCardAction')
    return React.cloneElement(children, {
      component: 'button',
      onClick: cancelBooking,
    })
  return children
})

const ClassCard = ({ start, end, live, feedItem = false, badgesTheme = 'MINIMAL', ...props }: Props): ReactElement => {
  // Determine if class is live based on input data
  // when 'live' is true, defaults to true when no
  // start and end time are provided, for the purpose
  // of programmatically setting the UI where needed
  const timeNow: number = +new Date()
  const isLive = start && end ? timeNow >= +new Date(start) && timeNow <= +new Date(end) : live
  const variantProps = { ...props, isLive, start, end, badgesTheme }
  return (
    <>
      {feedItem ? <Feed {...variantProps} /> : <Standard {...variantProps} />}
      {props.metaInfo && props.metaInfo.jsonLd ? (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: formatJsonLd(props.metaInfo.jsonLd) }} />
      ) : null}
    </>
  )
}
export default ClassCard
